<template>
  <div class="card__wrapper">
    <div class="card">
      <h1 class="card__title">{{ $t('messages.products') }}</h1>
      <div class="card__description">
        <div>
          <div class="mb-2">
            <div class="details-margin align-items-center">
              <div class="mr-3 d-flex align-items-center">
                <span class="font-weight-bold">{{ $t('messages.search') }}:</span>
                <b-form-input v-model="params.search" type="text" class="ml-3" />
              </div>
              <div class="mr-3 d-flex align-items-center mt-2">
                <span class="font-weight-bold">{{ $t('messages.distributor') }}:</span>
                <b-form-select v-model="params.distributorId" class="ml-3">
                  <option :value="null"></option>
                  <option v-for="distributor in distributors" :key="'d-' + distributor.id" :value="distributor.id">{{ distributor.name }}</option>
                </b-form-select>
              </div>
              <div class="mr-3 d-flex align-items-center mt-2">
                <span class="font-weight-bold">{{ $t('messages.publisher') }}:</span>
                <b-form-select v-model="params.publisher" class="ml-3">
                  <option :value="null"></option>
                  <option v-for="publisherChoice in publishers" :key="'p-' + publisherChoice.publisher" :value="publisherChoice.publisher">{{ publisherChoice.publisher }}</option>
                </b-form-select>
              </div>
              <div class="d-flex-responsive-order mt-2 mb-2">
                <div>
                  <b-form-checkbox v-model="params.order" class="checkbox-fix font-weight-bold">{{ $t('messages.order_extra') }}</b-form-checkbox>
                </div>
              </div>
            </div>
          </div>
          <b-table striped bordered stacked="md" :items="products" :fields="fields" v-if="products.length > 0">
            <template v-slot:cell(image)="row">
              <b-img :src="row.item.image" fluid :alt="row.item.name" @click="navigateToProduct(row.item)" class="pointer" />
            </template>

            <template v-slot:cell(distributor)="row">
              <div v-if="row.item.distributors.length > 0">
                <p v-for="distributor in row.item.distributors" :key="'distr-' + distributor.id"><b-link :href="distributor.url" target="_blank">{{ distributor.name }}</b-link></p>
              </div>
              <div v-if="row.item.distributors.length === 0">
                -
              </div>
            </template>

            <template v-slot:cell(type)="row">
              <div class="w-100 h-100 p-10 position-top" :class="{ 'bg-success': isProductPlentyInStock(row.item),
                   'bg-danger': !isProductInStock(row.item),
                   'bg-warning': isProductInStock(row.item) && !isProductPlentyInStock(row.item) }">
                <div v-if="row.item.preorder">
                  {{ $t('messages.preorder') }}
                </div>
                <div v-if="row.item.backorder">
                  {{ $t('messages.backorder') }}
                </div>
                <div v-if="!row.item.preorder && !row.item.backorder">
                  <div v-if="isProductPlentyInStock(row.item)">
                    {{ $t('messages.stock_in') }}
                  </div>
                  <div v-if="!isProductInStock(row.item)">
                    {{ $t('messages.sold_out') }}
                  </div>
                  <div v-if="isProductInStock(row.item) && !isProductPlentyInStock(row.item)">
                    {{ $t('messages.stock_almost_sold_out') }}
                  </div>
                </div>
              </div>
            </template>

            <template v-slot:cell(price)="row">
              € {{ row.item.price | round(2) }}
            </template>

            <template v-slot:cell(reduced_price)="row">
              € {{ row.item.reduced_price | round(2) }}
            </template>

            <template v-slot:cell(received)="row">
              <div class="d-flex">
                <b-form-input v-model="row.item.received" :number="true" type="number" :min="0" />
                <b-button variant="primary" class="d-flex buttonHeight position-relative" :disabled="!row.item.received" @click="updateStock(row.item)">
                  <font-awesome-icon icon="check"></font-awesome-icon>
                  <transition name="fade">
                    <div class="white-icon ml-2 check-position" v-if="row.item.marked">
                      <p class="white-iconfix">
                        <font-awesome-icon icon="check-circle" class="green vertical-align-middle"></font-awesome-icon>
                      </p>
                    </div>
                  </transition>
                </b-button>
              </div>
            </template>

            <template v-slot:cell(stock)="row">
              <span v-if="row.item.stock !== null">{{ row.item.stock }}</span>
              <span v-if="row.item.stock === null">-</span>
            </template>

            <template v-slot:cell(product_range)="row">
              <font-awesome-icon icon="check-circle" class="green" v-if="row.item.product_range" size="2x"></font-awesome-icon>
              <font-awesome-icon icon="times-circle" class="red" v-if="!row.item.product_range" size="2x"></font-awesome-icon>
            </template>

            <template v-slot:cell(total)="row">
              <span v-if="row.item.total !== null">{{ row.item.total }}</span>
              <span v-if="row.item.total === null">-</span>
            </template>

            <template v-slot:cell(reserved)="row">
              <span v-if="row.item.reserved !== null">{{ row.item.reserved }}</span>
              <span v-if="row.item.reserved === null">-</span>
            </template>

            <template v-slot:cell(expected_date)="row">
              <span v-if="row.item.expected_date !== null">{{ translateMonth(row.item.expected_date) }}</span>
              <span v-if="row.item.expected_date === null">-</span>
            </template>

            <template v-slot:cell(created_date)="row">
              {{ formatDate(row.item.created_date) }}
            </template>
          </b-table>
          <div class="load-more-wrapper">
            <b-button v-if="hasMore" variant="primary" class="w-100" @click="loadMore">
              {{ $t('messages.load_more') }}
            </b-button>
          </div>
        </div>
        <span v-if="products.length === 0" class="font-italic">{{ $t('messages.products_empty_all') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'

import { defaultMixin } from '../../mixin/default'
import { navigationMixin } from '../../mixin/navigation'
import { productMixin } from '../../mixin/product'

import { productService } from '../../services/product.service'

export default {
  name: 'products',
  metaInfo: function () {
    return {
      title: this.$i18n.t('messages.products') + ' - CrowdFinder',
      meta: [
        {
          'property': 'og:title',
          'content': this.$i18n.t('messages.products'),
          'vmid': 'og:title'
        }
      ]
    }
  },
  mixins: [ defaultMixin, navigationMixin, productMixin ],
  data: function () {
    return {
      fields: [{
        key: 'image',
        label: this.$i18n.t('messages.image'),
        tdClass: 'td-image'
      }, {
        key: 'name',
        label: this.$i18n.t('messages.name')
      }, {
        key: 'distributor',
        label: this.$i18n.t('messages.distributor'),
        tdClass: 'td-info'
      }, {
        key: 'type',
        label: this.$i18n.t('messages.type'),
        tdClass: 'td-info td-type'
      }, {
        key: 'price',
        label: this.$i18n.t('messages.price'),
        tdClass: 'td-info-small'
      }, {
        key: 'reduced_price',
        label: this.$i18n.t('messages.reduced_price'),
        tdClass: 'td-info-small'
      }, {
        key: 'ordered',
        label: this.$i18n.t('messages.ordered'),
        tdClass: 'td-info-small'
      }, {
        key: 'received',
        label: this.$i18n.t('messages.received'),
        tdClass: 'td-info'
      }, {
        key: 'to_order',
        label: this.$i18n.t('messages.to_order'),
        tdClass: 'td-info-small'
      }, {
        key: 'stock',
        label: this.$i18n.t('messages.stock'),
        tdClass: 'td-info-small'
      }, {
        key: 'product_range',
        label: this.$i18n.t('messages.product_range'),
        tdClass: 'td-info-small'
      }, {
        key: 'total',
        label: this.$i18n.t('messages.total'),
        tdClass: 'td-info-small'
      }, {
        key: 'reserved',
        label: this.$i18n.t('messages.reserved'),
        tdClass: 'td-info-small'
      }, {
        key: 'expected_date',
        label: this.$i18n.t('messages.expected_date'),
        tdClass: 'td-info'
      }],
      params: {
        search: this.$route.query.search ? this.$route.query.search : null,
        order: this.$route.query.order ? JSON.parse(this.$route.query.order) : false,
        distributorId: this.$route.query.distributorId ? parseInt(this.$route.query.distributorId) : null,
        publisher: this.$route.query.publisher ? this.$route.query.publisher : null,
        page: this.$route.query.page ? parseInt(this.$route.query.page) : 1
      },
      distributors: [],
      publishers: [],
      size: 5,
      initialized: false,
      hasMore: false,
      move: true,
      products: []
    }
  },
  methods: {
    formatDate: function (date) {
      let translatedDate = this.translateDate(date).split(' ')
      return translatedDate[0] + ' ' + translatedDate[1] + ' ' + translatedDate[2]
    },
    fetchProducts: function () {
      let loader = this.$loading.show()
      productService.fetchAll(this.initialized ? this.params.page : 1, this.initialized ? this.size : (this.params.page * this.size),
        this.params.search, this.params.distributorId, this.params.publisher, this.params.order)
        .then((products) => {
          products.forEach(function (product) {
            product.timeout = null
            product.marked = false
            if (product.ordered > 0) {
              product.received = product.ordered
            } else {
              product.received = null
            }
          })
          if (this.params.page === 1) {
            this.products = products
          } else {
            this.products = this.products.concat(products)
          }
          this.hasMore = products.length === (this.initialized ? this.size : (this.params.page * this.size))
          loader.hide()
          if (!this.initialized) {
            this.$store.commit('moveToScrollPosition')
          }
          this.initialized = true
        }, () => {
          this.$store.commit('error', 'errors.order_product')
          loader.hide()
          this.initialized = true
        })
    },
    fetchDistributors: function () {
      productService.fetchDistributors()
        .then((distributors) => {
          this.distributors = distributors
        }, () => {
          this.$store.commit('error', 'errors.distributor')
        })
    },
    fetchPublishers: function () {
      productService.fetchPublishers()
        .then((publishers) => {
          this.publishers = publishers
        }, () => {
          this.$store.commit('error', 'errors.publisher')
        })
    },
    updateStock: function (product) {
      productService.updateStock(product.id, product.received)
        .then(() => {
          product.marked = true
          clearTimeout(product.timeout)
          let time = 2000
          product.timeout = setTimeout(function () {
            product.marked = false
          }, time)
          this.initialized = false
          this.move = false
          this.products = []
          this.fetchProducts()
        }, () => {
          this.$store.commit('error', 'errors.stock_received')
        })
    },
    navigateToProduct: function (product) {
      this.$router.push('/product/' + this.slugText(product.id + '/' + product.name))
    },
    loadMore: function () {
      this.params.page += 1
      this.load()
    },
    resetAndLoad: function () {
      this.params.page = 1
      this.load()
    },
    load: function () {
      let params = this.params
      let query = this.generateQueryParams(params)
      this.pushUrlState(query)
      this.fetchProducts()
    }
  },
  watch: {
    'params.search': _.debounce(function () {
      this.resetAndLoad()
    }, 1000),
    'params.distributorId': function () {
      this.resetAndLoad()
    },
    'params.publisher': function () {
      this.resetAndLoad()
    },
    'params.order': function () {
      this.resetAndLoad()
    }
  },
  created: function () {
    this.fetchProducts()
    this.fetchDistributors()
    this.fetchPublishers()
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .buttonHeight {
    height: 36px;
  }

  .check-position {
    position: absolute !important;
    top: 11px !important;
    left: 8px !important;
  }

  .white-icon {
    background: #fff;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    position: relative;
    top: 4px;
  }

  .white-iconfix {
    position: relative;
    top: -6px;
    left: -2px;
  }

  .vertical-align-middle {
    vertical-align: middle;
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity .5s;
  }

  .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
  }

  .table >>> .td-image img {
    max-height: 100px !important;
  }

  .table >>> .td-type {
    position: relative;
    padding: 0px;
    word-break: break-word;
    display: flex !important;
    align-items: center;
  }

  .position-top {
    font-weight: bold;
    color: #ffffff
  }

  @media screen and (min-width: 768px) {
    .table >>> .td-image {
      width: 150px !important;
      text-align: center;
    }

    .table >>> .td-info {
      width: 150px !important;
    }

    .table >>> .td-info-small {
      width: 100px !important;
    }

    .table >>> .td-info-large {
      width: 300px !important;
    }

    .table >>> .td-type {
      position: relative;
      padding: 0px;
      word-break: break-word;
      display: table-cell !important;
    }

    .position-top {
      position: absolute;
      top: 0px;
      display: flex;
      align-items: center;
      font-weight: bold;
      color: #ffffff;
    }
  }

  .table >>> td {
    vertical-align: middle !important;
  }

  .checkbox-fix >>> label {
    padding-top: 2px !important;
  }
</style>



// WEBPACK FOOTER //
// src/pages/product/Products.vue